import { addDays, format, parseISO } from 'date-fns';

/**
 * E48 · Functional analysis on the portal — the hub's tab (the order of work: Set -> Assign the records -> Formulation ->
 * Plan a change -> Retire). The field lists the dynamic GraphQL client sends, the types of what comes back, and the pure
 * bits the screen shares (the roster order, the soft note, the pre-fill, the recurring groups, the payload builders).
 *
 * The portal does not bundle `@cc/domain`, so the choices (measure, function) are copied here as ids only — their words are
 * `fa.measure.*` / `fa.function.*` / `fa.functionLine.*`, never data. DISPLAY ONLY: counts and the client's own words, no
 * computed judgement. Every function that reads the clock takes it as a parameter (pinned in the tests).
 */

// ── the vocabulary (copy of @cc/domain's functionalAnalysis/constants.ts, ids only) ─────────────────

export const FA_MEASURES = ['frequency', 'duration', 'intensity'] as const;
export const FA_FUNCTIONS = [
  'escapeAvoidance',
  'attention',
  'tangible',
  'automaticRelief',
  'unknown',
] as const;
export type FaMeasure = (typeof FA_MEASURES)[number];
export type FaFunction = (typeof FA_FUNCTIONS)[number];
/** "Not sure yet" — the one function that stands alone. */
export const FA_FUNCTION_UNKNOWN: FaFunction = 'unknown';

/**
 * Q-e48f-1 A (2026-10-24, the 1.7.0 retest R-9): the function hypothesis is ONE OR MORE of the four, or "not sure yet" alone — a copy
 * of `@cc/domain`'s `toggleFaFunction` (ids only; model.test.ts keeps the two alike). Ticking "not sure yet" clears the others; any
 * other clears "not sure yet"; unticking the last leaves "not sure yet". The result is in the list's own order.
 */
export const toggleFaFunction = (
  current: readonly FaFunction[],
  fn: FaFunction,
): FaFunction[] => {
  if (fn === FA_FUNCTION_UNKNOWN) return [FA_FUNCTION_UNKNOWN];
  const on = current.includes(fn);
  const next = FA_FUNCTIONS.filter(
    (f) => f !== FA_FUNCTION_UNKNOWN && (f === fn ? !on : current.includes(f)),
  );
  return next.length ? next : [FA_FUNCTION_UNKNOWN];
};

/** The records are asked for, weekly, for this many weeks unless the therapist changes it (the board's example: 3). */
export const DEFAULT_ASSIGN_WEEKS = 3;
export const MAX_ASSIGN_WEEKS = 12;
/** A week of records comes before any change (the baseline). */
export const DEFAULT_BASELINE_WEEKS = 1;
/** The assign-the-records homework names the tool; the matcher completes each week by that week's first record. */
export const CHAIN_ANALYSIS_TOOL = 'chainAnalysis';

/** The sections, in the order of work. */
export const SECTIONS = [
  'set',
  'assign',
  'formulation',
  'plan',
  'retire',
] as const;
export type Section = (typeof SECTIONS)[number];

// ── the fields the dynamic client sends ─────────────────────────────────────────────────────────────

export const ROSTER_FIELDS = [
  'targetId',
  'name',
  'measure',
  'functionHypotheses',
  'addedByClient',
  'retired',
  'records',
  'threshold',
  'changes',
];

const COUNTED = ['text', 'count'];
const IDEA = ['instead', 'tool', 'times'];

export const SUMMARY_FIELDS = [
  'subject',
  'targetId',
  {
    target: [
      'name',
      'description',
      'measure',
      'functionHypotheses',
      'baselineWeeks',
      'addedByClient',
      'retired',
      'retiredReason',
    ],
  },
  'records',
  'threshold',
  'enough',
  'chains',
  'urgesOnly',
  {
    periods: [
      'kind',
      'changeId',
      'from',
      'to',
      { weeks: ['weekStart', 'done', 'urgeOnly', 'meanUrge'] },
      { totals: ['days', 'done', 'urgeOnly', 'meanUrge', 'perWeek'] },
      { changeDays: ['yes', 'partly', 'no', 'marked'] },
    ],
  },
  {
    changes: [
      'changeId',
      'whatChanges',
      'expectedEffect',
      'startsOn',
      'byClient',
      { doneDays: ['yes', 'partly', 'no'] },
      'markedDays',
    ],
  },
  { days: ['date', 'count', 'urge', 'urgeOnly', 'from'] },
  {
    recurring: [
      { promptingEvents: COUNTED },
      { vulnerabilities: COUNTED },
      {
        links: [
          { event: COUNTED },
          { thought: COUNTED },
          { feeling: COUNTED },
          { body: COUNTED },
          { action: COUNTED },
        ],
      },
      { rightAway: COUNTED },
      { later: COUNTED },
      { nextTime: IDEA },
    ],
  },
  { suggestion: IDEA },
];

export const COMMAND_FIELDS = ['id', 'recorded'];

// ── the shapes that come back ───────────────────────────────────────────────────────────────────────

export type RosterRow = {
  targetId: string;
  name: string;
  measure: string;
  functionHypotheses: string[];
  addedByClient: boolean;
  retired: boolean;
  records: number;
  threshold: number;
  changes: number;
};

export type Counted = { text: string; count: number };
export type Idea = { instead: string; tool?: string | null; times: number };
export type DoneDays = { yes: number; partly: number; no: number };

export type SummaryWeek = {
  weekStart: string;
  done: number;
  urgeOnly: number;
  meanUrge?: number | null;
};
export type SummaryPeriod = {
  kind: 'before' | 'after' | string;
  changeId?: string | null;
  from?: string | null;
  to?: string | null;
  weeks: SummaryWeek[];
  totals: {
    days: number;
    done: number;
    urgeOnly: number;
    meanUrge?: number | null;
    perWeek?: number | null;
  };
  changeDays?: (DoneDays & { marked: number }) | null;
};
export type SummaryChange = {
  changeId: string;
  whatChanges: string;
  expectedEffect?: string | null;
  startsOn: string;
  byClient: boolean;
  doneDays: DoneDays;
  markedDays: number;
};
export type SummaryDay = {
  date: string;
  count: number;
  urge?: number | null;
  urgeOnly: number;
  from: string;
};
export type Recurring = {
  promptingEvents: Counted[];
  vulnerabilities: Counted[];
  links: Record<LinkKind, Counted[]>;
  rightAway: Counted[];
  later: Counted[];
  nextTime: Idea[];
};
export type SummaryRow = {
  subject: string;
  targetId: string;
  target: {
    name: string;
    description?: string | null;
    measure: string;
    functionHypotheses: string[];
    baselineWeeks: number;
    addedByClient: boolean;
    retired: boolean;
    retiredReason?: string | null;
  };
  records: number;
  threshold: number;
  enough: boolean;
  chains: number;
  urgesOnly: number;
  periods: SummaryPeriod[];
  changes: SummaryChange[];
  days: SummaryDay[];
  recurring: Recurring;
  suggestion?: Idea | null;
};

export const LINK_KINDS = [
  'event',
  'thought',
  'feeling',
  'body',
  'action',
] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

// ── the roster (the selector) ───────────────────────────────────────────────────────────────────────

/** Active behaviours first (as the API sent them), the closed ones after — the closed keep their records. */
export const orderedRoster = (rows: readonly RosterRow[]): RosterRow[] => [
  ...rows.filter((r) => !r.retired),
  ...rows.filter((r) => r.retired),
];

/** How many behaviours are being tracked now — the API refuses a fourth (FA_TARGET_TOO_MANY); the screen only shows. */
export const activeCount = (rows: readonly RosterRow[]): number =>
  rows.filter((r) => !r.retired).length;

/** The selected behaviour: the one asked for if it is still on the roster, else the first active, else the first closed. */
export const selectedTarget = (
  rows: readonly RosterRow[],
  wanted: string | null,
): RosterRow | null => {
  const ordered = orderedRoster(rows);
  return ordered.find((r) => r.targetId === wanted) ?? ordered[0] ?? null;
};

// ── the soft note and the pre-fill ──────────────────────────────────────────────────────────────────

/**
 * The soft note on Plan a change: under `threshold` records it says so — a WARNING, never a block. `threshold` and `enough`
 * are the API's (never a literal here).
 */
export const softNote = (
  s: Pick<SummaryRow, 'records' | 'threshold' | 'enough'> | null | undefined,
): { show: boolean; n: number; max: number } =>
  s
    ? { show: !s.enough, n: s.records, max: s.threshold }
    : { show: false, n: 0, max: 0 };

/** What changes starts as the suggestion (the most-marked next-time idea), editable. */
export const prefillOf = (
  suggestion: Idea | null | undefined,
): { text: string; times: number; tool: string | null } | null =>
  suggestion && suggestion.instead.trim()
    ? {
        text: suggestion.instead,
        times: suggestion.times,
        tool: suggestion.tool ?? null,
      }
    : null;

// ── dates and figures ───────────────────────────────────────────────────────────────────────────────

export const dayOf = (d: Date): string => format(d, 'yyyy-MM-dd');

/** The change's default start: tomorrow, a local date (the clock is injected). */
export const defaultStart = (now: Date): string => dayOf(addDays(now, 1));

export const oneDecimal = (n: number): string =>
  String(Math.round(n * 10) / 10);

/** "5 Oct" — a local date as the hub shows it. */
export const shortDate = (iso: string, locale: Parameters<typeof format>[2]) =>
  format(parseISO(iso), 'd MMM', locale);

export const fullDate = (iso: string, locale: Parameters<typeof format>[2]) =>
  format(parseISO(iso), 'd MMM yyyy', locale);

/** A week with nothing in it shows no urge figure (never a 0). */
export const urgeFigure = (n: number | null | undefined): string | null =>
  n === null || n === undefined ? null : oneDecimal(n);

/** The period's title key and its date: Before the change · After (from <date>). */
export const periodHeading = (
  p: Pick<SummaryPeriod, 'kind' | 'from'>,
): { key: 'before' | 'after'; from: string | null } =>
  p.kind === 'after'
    ? { key: 'after', from: p.from ?? null }
    : { key: 'before', from: null };

/** The change a period starts at, so its day taps sit beside it. */
export const changeOfPeriod = (
  p: Pick<SummaryPeriod, 'changeId'>,
  changes: readonly SummaryChange[],
): SummaryChange | null =>
  p.changeId ? changes.find((c) => c.changeId === p.changeId) ?? null : null;

/**
 * "done on {{done}} of {{marked}} days (yes n · partly m)": `done` counts the days tapped YES — the board's scenario reads "done on 2
 * of 3 days, partly on 1" (yes 2 · partly 1 · marked 3), and the phone's "done on X of Y" counts the same way (faModel `doneYes`).
 */
export const changeDaysArgs = (c: {
  doneDays: DoneDays;
  markedDays: number;
}) => ({
  done: c.doneDays.yes,
  marked: c.markedDays,
  yes: c.doneDays.yes,
  partly: c.doneDays.partly,
});

/** A merged day: the API said it came (at least in part) from the diary card. */
export const fromDiaryCard = (day: Pick<SummaryDay, 'from'>): boolean =>
  /diary card/i.test(day.from);

// ── "what recurs" ───────────────────────────────────────────────────────────────────────────────────

export type RecurringRow = { text: string; count: number; tool: string | null };

export type RecurringGroup = {
  /** a stable id for test ids: vulnerabilities · promptingEvents · link-event … · rightAway · later · nextTime */
  id: string;
  /** the i18n key of its heading (`fa.hub.*`) */
  headingKey: string;
  /** the one-line hint of the phone (`fa.record.hint.*`), for the three that carry one */
  hintKey: string | null;
  rows: RecurringRow[];
};

/**
 * The groups of "What recurs", in the order of the chain: Before (vulnerabilities) · What set it off · the five links under
 * their plain headings · Right away · Later · Next time I could change. A group with nothing in it is left out.
 */
export const recurringGroups = (
  r: Recurring | null | undefined,
): RecurringGroup[] => {
  if (!r) return [];
  const counted = (rows: readonly Counted[]) =>
    rows.map((x) => ({ text: x.text, count: x.count, tool: null }));
  const groups: RecurringGroup[] = [
    {
      id: 'vulnerabilities',
      headingKey: 'fa.hub.vulnerabilities',
      hintKey: null,
      rows: counted(r.vulnerabilities),
    },
    {
      id: 'promptingEvents',
      headingKey: 'fa.hub.promptingEvents',
      hintKey: null,
      rows: counted(r.promptingEvents),
    },
    ...LINK_KINDS.map((k) => ({
      id: `link-${k}`,
      headingKey: `fa.hub.link.${k}`,
      hintKey: null,
      rows: counted(r.links[k]),
    })),
    {
      id: 'rightAway',
      headingKey: 'fa.hub.rightAway',
      hintKey: 'fa.record.hint.rightAway',
      rows: counted(r.rightAway),
    },
    {
      id: 'later',
      headingKey: 'fa.hub.later',
      hintKey: 'fa.record.hint.later',
      rows: counted(r.later),
    },
    {
      id: 'nextTime',
      headingKey: 'fa.hub.nextTime',
      hintKey: 'fa.record.hint.nextTime',
      rows: r.nextTime.map((x) => ({
        text: x.instead,
        count: x.times,
        tool: x.tool ?? null,
      })),
    },
  ];
  return groups.filter((g) => g.rows.length > 0);
};

// ── the commands ────────────────────────────────────────────────────────────────────────────────────

export type TargetDraft = {
  name: string;
  description: string;
  measure: FaMeasure;
  functionHypotheses: FaFunction[];
  baselineWeeks: number;
};

export const emptyDraft = (): TargetDraft => ({
  name: '',
  description: '',
  measure: 'frequency',
  functionHypotheses: [FA_FUNCTION_UNKNOWN],
  baselineWeeks: DEFAULT_BASELINE_WEEKS,
});

/** SetTargetBehavior — `subject` rides the therapist's act; `performer` is never on the wire (the session's). */
export const setInputOf = (clientId: string, d: TargetDraft) => ({
  subject: clientId,
  name: d.name.trim(),
  description: d.description.trim() || null,
  measure: d.measure,
  functionHypotheses: d.functionHypotheses,
  baselineWeeks: d.baselineWeeks,
});

export type ChangeDraft = {
  whatChanges: string;
  expectedEffect: string;
  startsOn: string;
};

export const planInputOf = (
  clientId: string,
  targetId: string,
  d: ChangeDraft,
) => ({
  subject: clientId,
  targetId,
  whatChanges: d.whatChanges.trim(),
  expectedEffect: d.expectedEffect.trim() || null,
  startsOn: d.startsOn,
});

/** The plan needs something to change and a date — nothing else (too few records is never a refusal). */
export const planProblem = (d: ChangeDraft): boolean =>
  !d.whatChanges.trim() || !d.startsOn;

export const retireInputOf = (
  clientId: string,
  targetId: string,
  reason: string,
) => ({
  subject: clientId,
  targetId,
  reason: reason.trim() || null,
});

/** Weekly for N weeks from tomorrow, 18:00 in the CLIENT's zone (the E34 repeat rule; the board's example). */
export const recordsSchedule = (
  from: Date,
  weeks: number,
  tzid: string,
): { rrule: string; dtstart: string; tzid: string; exdates: string[] } => ({
  rrule: `FREQ=WEEKLY;COUNT=${weeks}`,
  dtstart: `${dayOf(addDays(from, 1))}T18:00:00`,
  tzid,
  exdates: [],
});

export const clampWeeks = (n: number): number =>
  Number.isInteger(n) && n > 0
    ? Math.min(n, MAX_ASSIGN_WEEKS)
    : DEFAULT_ASSIGN_WEEKS;

/** AssignHomework — kind tool, target chainAnalysis naming the behaviour. */
export const assignInputOf = (a: {
  clientId: string;
  targetId: string;
  title: string;
  instructions: string;
  weeks: number;
  now: Date;
  tzid: string;
}) => ({
  subject: a.clientId,
  kind: 'tool',
  target: { type: CHAIN_ANALYSIS_TOOL, ref: a.targetId },
  title: a.title.trim(),
  instructions: a.instructions.trim() || null,
  reminderPolicy: 'default',
  activityTarget: null,
  paperAllowed: false,
  dueAt: null,
  schedule: recordsSchedule(a.now, clampWeeks(a.weeks), a.tzid),
});
